import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import {
  EmptyState,
  InfoCard,
  Link,
  Progress,
  StatusAborted,
  StatusError,
  StatusOK,
  StatusPending,
  StatusRunning,
  WarningPanel,
} from '@backstage/core-components';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

type MergeRequestInsight = {
  iid: number;
  title: string;
  url: string;
  author?: string;
  state: string;
  updatedAt: string;
  pipeline?: {
    status: string;
    url?: string;
    failedJobs?: { name: string; stage?: string; url?: string }[];
  };
  sonarqube?: {
    gate: string;
    newIssues?: number;
    newCoverage?: number;
    newDuplicatedLinesDensity?: number;
    url: string;
  };
};

const PipelineStatus = ({
  pipeline,
}: {
  pipeline?: MergeRequestInsight['pipeline'];
}) => {
  if (!pipeline) {
    return <Typography variant="body2">—</Typography>;
  }
  let status;
  switch (pipeline.status) {
    case 'success':
      status = <StatusOK>success</StatusOK>;
      break;
    case 'failed':
      status = <StatusError>failed</StatusError>;
      break;
    case 'running':
      status = <StatusRunning>running</StatusRunning>;
      break;
    case 'pending':
    case 'created':
    case 'waiting_for_resource':
      status = <StatusPending>{pipeline.status}</StatusPending>;
      break;
    default:
      status = <StatusAborted>{pipeline.status}</StatusAborted>;
  }
  const linked = pipeline.url ? (
    <Link to={pipeline.url}>{status}</Link>
  ) : (
    status
  );
  if (!pipeline.failedJobs?.length) {
    return linked;
  }
  return (
    <>
      {linked}
      <Typography variant="caption" component="div" color="textSecondary">
        {pipeline.failedJobs.map((job, index) => (
          <span key={job.name}>
            {index > 0 && ', '}
            {job.url ? <Link to={job.url}>{job.name}</Link> : job.name}
          </span>
        ))}
      </Typography>
    </>
  );
};

const SonarqubeStatus = ({
  sonarqube,
}: {
  sonarqube?: MergeRequestInsight['sonarqube'];
}) => {
  if (!sonarqube) {
    return <Typography variant="body2">—</Typography>;
  }
  const detail =
    sonarqube.newIssues !== undefined
      ? ` (${sonarqube.newIssues} new issues)`
      : '';
  const status =
    sonarqube.gate === 'OK' ? (
      <StatusOK>gate passed{detail}</StatusOK>
    ) : (
      <StatusError>
        gate {sonarqube.gate.toLowerCase()}
        {detail}
      </StatusError>
    );
  return <Link to={sonarqube.url}>{status}</Link>;
};

export const EntityMergeRequestsCard = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const { kind, namespace = 'default', name } = {
    kind: entity.kind,
    namespace: entity.metadata.namespace,
    name: entity.metadata.name,
  };

  const { value, loading, error } = useAsync(async (): Promise<
    MergeRequestInsight[]
  > => {
    const baseUrl = await discoveryApi.getBaseUrl('health');
    const response = await fetch(
      `${baseUrl}/mr-insights/${encodeURIComponent(
        namespace,
      )}/${encodeURIComponent(kind)}/${encodeURIComponent(name)}?limit=10`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => undefined);
      throw new Error(
        body?.error ??
          `Request failed with ${response.status} ${response.statusText}`,
      );
    }
    return (await response.json()).mergeRequests ?? [];
  }, [kind, namespace, name]);

  let content;
  if (loading) {
    content = <Progress />;
  } else if (error) {
    content = (
      <WarningPanel
        title="Could not load merge requests"
        message={error.message}
      />
    );
  } else if (!value || value.length === 0) {
    content = (
      <EmptyState
        title="No open merge requests"
        missing="content"
        description="This project has no open merge requests."
      />
    );
  } else {
    content = (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Merge request</TableCell>
            <TableCell>Author</TableCell>
            <TableCell>Updated</TableCell>
            <TableCell>Pipeline</TableCell>
            <TableCell>SonarQube</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {value.map(mr => (
            <TableRow key={mr.iid}>
              <TableCell>
                <Link to={mr.url}>
                  <Typography variant="body2">
                    !{mr.iid} {mr.title}
                  </Typography>
                </Link>
              </TableCell>
              <TableCell>{mr.author ?? '—'}</TableCell>
              <TableCell>
                {new Date(mr.updatedAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <PipelineStatus pipeline={mr.pipeline} />
              </TableCell>
              <TableCell>
                <SonarqubeStatus sonarqube={mr.sonarqube} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return <InfoCard title="Open merge requests">{content}</InfoCard>;
};
